import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Lightbulb, Home, Trophy, Share2, Star, ChevronsUp } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { shareScoreCard } from "@/lib/shareCard";
import { LEVELS_META, nextTier } from "@/game/config";
import ReviewModal from "@/components/ReviewModal";
import RewardModal from "@/components/RewardModal";
import BadgeModal from "@/components/BadgeModal";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;
const CORDOBA_PTS = 75;
const CELEBRATION_MS = 13000;
const TIER_COLOR = { facil: "#4CAF50", normal: "#0067C6", extremo: "#E91E63" };
const TIER_LABEL = { facil: "EASY", normal: "NORMAL", extremo: "☠️ EXTREMO" };

// Piñatas swinging across the top of the screen.
const PINATAS = [
  { left: "8%", delay: "0s" },
  { left: "30%", delay: "0.4s" },
  { left: "52%", delay: "0.2s" },
  { left: "74%", delay: "0.6s" },
  { left: "90%", delay: "0.3s" },
];

export default function VictoryCelebration({ character, result, level, cordobas = 0, difficulty = "normal", mode = "normal", dailyDate = "", onSuggest, onMenu, onNext, onStepUp, hasNext = false, challenge = null }) {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const isFinale = !hasNext; // final level keeps the big "you finished Nicaragua" finale
  const stepTier = mode === "normal" ? nextTier(difficulty) : null;
  const [phase, setPhase] = useState("party"); // party -> soon (finale only)
  const [showReview, setShowReview] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [reward, setReward] = useState(null);
  const [badge, setBadge] = useState(null);
  const audioRef = useRef(null);
  const savedRef = useRef(false);

  const totalScore = result.score + cordobas * CORDOBA_PTS;

  const shareVictory = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const res = await shareScoreCard({
        score: totalScore,
        characterName: character.name,
        levelName: level.name,
        charColor: character.colors?.accent || "#FFEB3B",
        lang,
        levelIndex: level.index,
        charId: character.id,
        diffId: difficulty,
        playerName: user?.username || "",
      });
      if (res === "downloaded") toast.success(t("cardSaved"));
      else if (res === "failed") toast.error(t("cardShareFail"));
    } finally {
      setSharing(false);
    }
  };

  // Auto-save the score for logged-in players (final-level scores still count).
  useEffect(() => {
    if (user && !savedRef.current) {
      savedRef.current = true;
      api.saveScore({
        player_name: user.username,
        character: character.name,
        level_index: level.index,
        level_name: level.name,
        score: totalScore,
        cordobas,
        completed: true,
        difficulty,
        time_ms: result.timeMs || 0,
        mode,
        daily_date: mode === "daily" ? dailyDate : "",
      }).catch(() => {});
      if (mode === "normal") {
        api.completeLevelTier({ level_index: level.index, difficulty, character_id: character?.id }).then((r) => {
          if (r.data?.awarded > 0) { toast.success(t("coinsEarned")(r.data.awarded)); }
          if (r.data?.unlocked_character) { setReward(r.data.unlocked_character); }
          if (r.data?.badge_awarded) { setBadge(r.data.badge_awarded); }
          if (r.data?.awarded > 0 || r.data?.unlocked_character || r.data?.badge_awarded) { refreshUser?.(); }
        }).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Music: Bebiendo Toda la Noche (song.mp3)
  useEffect(() => {
    const a = new Audio(`${base}/bebiendo-toda-la-noche.mp3`);
    a.volume = 0.85;
    a.loop = true;
    a.play().catch(() => {});
    audioRef.current = a;
    return () => { try { a.pause(); a.currentTime = 0; } catch (e) { /* noop */ } };
  }, []);

  // Confetti bursts during the party phase.
  useEffect(() => {
    const colors = ["#0067C6", "#ffffff", "#FFEB3B", "#FF5722", "#4CAF50", "#FF1744"];
    const fire = () => {
      confetti({ particleCount: 60, spread: 80, origin: { x: 0, y: 0.6 }, angle: 60, colors });
      confetti({ particleCount: 60, spread: 80, origin: { x: 1, y: 0.6 }, angle: 120, colors });
      confetti({ particleCount: 40, spread: 360, startVelocity: 35, origin: { x: 0.5, y: 0.4 }, colors });
    };
    fire();
    const iv = setInterval(fire, 900);
    // Non-final levels: shorter party, no "soon" finale phase (player taps Next Level).
    const partyMs = isFinale ? CELEBRATION_MS : 4000;
    const stop = setTimeout(() => clearInterval(iv), partyMs);
    const toSoon = isFinale ? setTimeout(() => setPhase("soon"), CELEBRATION_MS) : null;
    return () => { clearInterval(iv); clearTimeout(stop); if (toSoon) clearTimeout(toSoon); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="absolute inset-0 z-[70] overflow-hidden"
      style={{ background: "radial-gradient(ellipse at 50% 40%, #1b2a4a 0%, #0a0a14 75%)" }}
      data-testid="victory-celebration">

      {/* Piñatas */}
      {PINATAS.map((p, i) => (
        <div key={i} className="absolute top-0 text-3xl md:text-6xl nb-swing" style={{ left: p.left, animationDelay: p.delay }} aria-hidden>
          <span className="block" style={{ marginTop: "-6px" }}>🪅</span>
        </div>
      ))}

      {/* Brand watermark (party phase only — avoids overlapping the finale buttons) */}
      {phase === "party" && (
        <img
          src={`${base.replace("/game-assets", "")}/icon-192.png`}
          alt="Nica Bros"
          data-testid="celebration-logo-watermark"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-xl opacity-80 pointer-events-none shadow-lg"
        />
      )}

      {phase === "party" ? (
        <div className="absolute inset-0 z-10 overflow-y-auto">
        <div className="min-h-full flex flex-col items-center justify-center text-center nb-pop p-4 py-8" data-testid="celebration-party">
          <p className="font-pixel text-[#FFEB3B] text-[10px] md:text-base pixel-stroke tracking-widest mb-1 flex items-center gap-2">
            <Trophy size={14} strokeWidth={2.5} /> {isFinale ? t("finaleTag") : t("levelCompleteTag")}
          </p>
          <h1 className="font-pixel text-xl sm:text-3xl md:text-5xl text-white pixel-stroke leading-tight">
            {isFinale ? t("finaleTitle") : t("levelCompleteTitle")}
          </h1>

          {/* Dancing characters */}
          <div className="flex items-end justify-center gap-2 md:gap-6 mt-2 md:mt-4">
            <img src={`${base}/${character.id}.webp`} alt={character.name} draggable={false}
              className="nb-dance" style={{ height: "min(20vh, 150px)", filter: "drop-shadow(0 10px 12px rgba(0,0,0,0.55))" }} />
          </div>

          <p className="font-vt text-white/85 text-base md:text-xl mt-2">
            {character.name} · <span className="text-[#FFEB3B]">{totalScore.toLocaleString()} {t("ptsShort")}</span>
          </p>
          {cordobas > 0 && (
            <p className="font-vt text-[#4CAF50] text-sm md:text-lg mt-0.5" data-testid="victory-bonus-breakdown">
              🏂 {t("bonusTitle")}: C$ {cordobas} → +{(cordobas * CORDOBA_PTS).toLocaleString()} {t("ptsShort")}
            </p>
          )}
          <p className="font-vt text-white/60 text-sm md:text-lg mt-0.5 short-hide">🎶 Bebiendo Toda la Noche 🍺</p>

          {challenge && challenge.score > 0 && (
            <div
              data-testid="challenge-result-banner"
              className="mt-2 px-3 py-1.5 rounded-md font-vt text-base leading-tight max-w-sm"
              style={{ backgroundColor: totalScore > challenge.score ? "rgba(76,175,80,0.22)" : "rgba(233,30,99,0.22)", border: `2px solid ${totalScore > challenge.score ? "#4CAF50" : "#E91E63"}`, color: "#fff" }}
            >
              {totalScore > challenge.score
                ? t("challengeYouWon")(challenge.name || "?")
                : t("challengeYouLost")(challenge.name || "?", challenge.score.toLocaleString())}
            </div>
          )}

          {isFinale ? (
            <div className="flex flex-col items-center gap-2.5 mt-3 md:mt-6 w-full max-w-xs">
              <button data-testid="celebration-share-btn" onClick={shareVictory}
                className="pixel-btn bg-[#0067C6] text-white w-full px-7 py-3 text-sm md:text-base flex items-center justify-center gap-2">
                <Share2 size={16} strokeWidth={2.5} /> {t("shareVictory")}
              </button>
              {stepTier && (
                <button data-testid="celebration-step-up-btn" onClick={() => onStepUp?.(stepTier)}
                  className="pixel-btn text-white w-full px-6 py-3 text-sm flex items-center justify-center gap-1.5" style={{ backgroundColor: TIER_COLOR[stepTier] }}>
                  <ChevronsUp size={15} strokeWidth={2.5} /> {t("playOnTier")(TIER_LABEL[stepTier])}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2.5 mt-3 md:mt-5 w-full max-w-xs">
              {LEVELS_META[level.index + 1] && (
                <div data-testid="next-level-teaser" className="font-pixel text-[10px] md:text-xs text-[#FFEB3B] pixel-stroke px-3 py-1.5 rounded-full bg-[#FF5722]/20 ring-1 ring-[#FFEB3B]/40 animate-pulse">
                  {t("levelUnlocked")(LEVELS_META[level.index + 1].name)}
                </div>
              )}
              <button data-testid="celebration-next-btn" onClick={onNext}
                className="pixel-btn bg-[#FF5722] text-white w-full px-6 py-3.5 text-base md:text-lg blink flex items-center justify-center gap-2">
                {t("nextLevel")}
              </button>
              {stepTier && (
                <button data-testid="celebration-step-up-btn" onClick={() => onStepUp?.(stepTier)}
                  className="pixel-btn text-white w-full px-6 py-3 text-sm flex items-center justify-center gap-1.5" style={{ backgroundColor: TIER_COLOR[stepTier] }}>
                  <ChevronsUp size={15} strokeWidth={2.5} /> {t("playOnTier")(TIER_LABEL[stepTier])}
                </button>
              )}
              <div className="grid grid-cols-2 gap-2.5 w-full">
                <button data-testid="celebration-share-btn" onClick={shareVictory}
                  className="pixel-btn bg-[#0067C6] text-white w-full py-3 text-xs flex items-center justify-center gap-1.5">
                  <Share2 size={14} strokeWidth={2.5} /> {t("shareScore")}
                </button>
                <button data-testid="celebration-menu-btn" onClick={onMenu}
                  className="pixel-btn bg-[#222] text-white w-full py-3 text-xs flex items-center justify-center gap-1.5">
                  <Home size={14} strokeWidth={2.5} /> {t("menu")}
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      ) : (
        <div className="absolute inset-0 z-10 overflow-y-auto">
        <div className="min-h-full flex flex-col items-center justify-center p-4 py-8">
        <div className="relative flex flex-col items-center text-center pixel-card bg-[#141414]/95 max-w-sm w-full p-5 nb-pop" data-testid="celebration-soon">
          <span className="text-3xl mb-1">🎉</span>
          <h2 className="font-pixel text-base md:text-lg text-[#FFEB3B] pixel-stroke leading-tight" data-testid="level4-coming-soon">
            {t("finishedThanksTitle")}
          </h2>
          <p className="font-vt text-white/85 text-sm md:text-base mt-1.5 leading-snug">{t("finishedThanksBody")}</p>

          <div className="flex flex-col gap-2 w-full mt-4">
            <button data-testid="celebration-review-btn" onClick={() => setShowReview(true)}
              className="pixel-btn bg-[#FFB300] text-black w-full py-2.5 text-xs flex items-center justify-center gap-2">
              <Star size={15} strokeWidth={2.5} fill="#000" /> {t("rateGame")}
            </button>
            <button data-testid="celebration-suggest-btn" onClick={onSuggest}
              className="pixel-btn bg-[#4CAF50] text-white w-full py-2.5 text-xs flex items-center justify-center gap-2">
              <Lightbulb size={15} strokeWidth={2.5} /> {t("helpCreate")}
            </button>
            <button data-testid="celebration-share-btn-soon" onClick={shareVictory}
              className="pixel-btn bg-[#0067C6] text-white w-full py-2.5 text-xs flex items-center justify-center gap-2">
              <Share2 size={15} strokeWidth={2.5} /> {t("shareVictory")}
            </button>
            <button data-testid="celebration-menu-btn" onClick={onMenu}
              className="pixel-btn bg-[#FF5722] text-white w-full py-2.5 text-xs flex items-center justify-center gap-2">
              <Home size={15} strokeWidth={2.5} /> {t("menu")}
            </button>
          </div>
        </div>
        </div>
        </div>
      )}
      {showReview && <ReviewModal onClose={() => setShowReview(false)} intro={t("reviewIntroFinish")} />}
      {reward && <RewardModal charId={reward.id} charName={reward.name} onClose={() => setReward(null)} />}
      {badge && <BadgeModal badge={badge} onClose={() => setBadge(null)} />}
    </div>
  );
}
